"use client";
import { motion, useAnimation, useInView } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import {
  FaMobile,
  FaGlobe,
  FaUsers,
  FaChartLine,
  FaServer,
  FaCode,
} from "react-icons/fa";

const metrics = [
  {
    id: 1,
    targetValue: 60,
    suffix: "+",
    label: "Websites Developed",
    icon: <FaGlobe />,
    description:
      "Custom web development solutions crafted for diverse industries to maximize online presence and performance.",
    duration: 2,
  },
  {
    id: 2,
    targetValue: 15,
    suffix: "+",
    label: "Mobile Apps Built",
    icon: <FaMobile />,
    description:
      "High-quality iOS and Android mobile app development with intuitive UX and high user ratings.",
    duration: 1.8,
  },
  {
    id: 3,
    targetValue: 20,
    suffix: "+",
    label: "Active Clients",
    icon: <FaUsers />,
    description:
      "Businesses we currently support with website design, SEO optimization, and ongoing digital strategy.",
    duration: 1.5,
  },
  {
    id: 4,
    targetValue: 100,
    suffix: "%",
    label: "SEO Success",
    icon: <FaChartLine />,
    description:
      "Client satisfaction achieved through effective search engine optimization and comprehensive digital marketing services.",
    duration: 2.2,
  },
  {
    id: 5,
    targetValue: 24,
    suffix: "/7",
    label: "Support",
    icon: <FaServer />,
    description:
      "24/7 technical support, website maintenance, and prompt updates to keep your business online and secure.",
    duration: 1.2,
  },
  {
    id: 6,
    targetValue: 1,
    suffix: "M+",
    label: "Lines of Code",
    icon: <FaCode />,
    description:
      "Robust, scalable coding powering our web and mobile solutions for seamless user experiences.",
    duration: 2.5,
  },
];

const CountUpNumber = ({
  targetValue,
  suffix,
  duration,
}: {
  targetValue: number;
  suffix: string;
  duration: number;
}) => {
  const [count, setCount] = useState(0);
  const controls = useAnimation();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });

  useEffect(() => {
    if (inView) {
      controls.start({
        opacity: 1,
        transition: { duration: 0.5 },
      });

      const increment = targetValue / (duration * 60); // 60fps
      let currentCount = 0;

      const timer = setInterval(() => {
        currentCount += increment;
        if (currentCount >= targetValue) {
          currentCount = targetValue;
          clearInterval(timer);
        }
        setCount(Math.floor(currentCount));
      }, 1000 / 60); // 60fps

      return () => clearInterval(timer);
    }
  }, [inView, targetValue, duration, controls]);

  return (
    <motion.span
      ref={ref}
      initial={{ opacity: 0 }}
      animate={controls}
      className="impact__value"
    >
      {count}
      {suffix}
    </motion.span>
  );
};

export default function OurImpact() {
  return (
    <section id="our-impact" className="impact">
      <div className="impact__container">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="impact__header"
        >
          <h2 className="impact__title">
            Our <span className="impact__title-accent">Impact</span>
          </h2>
          <p className="impact__subtitle">
            Quantifying our contribution to digital transformation
          </p>
        </motion.div>

        <div className="impact__grid">
          {metrics.map((metric, index) => (
            <motion.div
              key={metric.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="impact__card"
            >
              <div className="impact__icon-wrapper">{metric.icon}</div>
              <h3 className="impact__value">
                <CountUpNumber
                  targetValue={metric.targetValue}
                  suffix={metric.suffix}
                  duration={metric.duration}
                />
              </h3>
              <h4 className="impact__label">{metric.label}</h4>
              <p className="impact__description">{metric.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
